import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { initAnalytics, trackPageView } from "@/lib/analytics";

import Landing from "@/pages/Landing";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import AdminLogin from "@/pages/AdminLogin";
import AdminLeads from "@/pages/AdminLeads";
import AdminPosts from "@/pages/AdminPosts";
import AdminPostEditor from "@/pages/AdminPostEditor";
import NotFound from "@/pages/NotFound";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import ProtectedAdmin from "@/components/ProtectedAdmin";
import CookieConsent from "@/components/CookieConsent";

function AnalyticsRouteListener() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    let lenis;
    let raf;
    (async () => {
      const Lenis = (await import("lenis")).default;
      lenis = new Lenis({
        duration: 1.15,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
      });
      const loop = (time) => {
        lenis.raf(time);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
      window.__lenis = lenis;
    })();
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (lenis) lenis.destroy();
    };
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App noise">
          <BrowserRouter>
            <AnalyticsRouteListener />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin/leads"
                element={
                  <ProtectedAdmin>
                    <AdminLeads />
                  </ProtectedAdmin>
                }
              />
              <Route
                path="/admin/posts"
                element={
                  <ProtectedAdmin>
                    <AdminPosts />
                  </ProtectedAdmin>
                }
              />
              <Route
                path="/admin/posts/new"
                element={
                  <ProtectedAdmin>
                    <AdminPostEditor />
                  </ProtectedAdmin>
                }
              />
              <Route
                path="/admin/posts/:id/edit"
                element={
                  <ProtectedAdmin>
                    <AdminPostEditor />
                  </ProtectedAdmin>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
          <CookieConsent />
          <Toaster
            position="bottom-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "var(--bg-2)",
                border: "1px solid var(--border-dim)",
                color: "var(--text-1)",
                fontFamily: "Manrope, sans-serif",
              },
            }}
          />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
